import { useEffect, useCallback, useRef } from 'react';
import { useDataProvider } from 'react-admin';
/**
 * Get a callback to subscribe to events on a topic and optionally unsubscribe on unmount.
 *
 * @param {string} topic The topic to subscribe to
 * @param {SubscriptionCallback<RecordEvent>} callback The callback to call when an event is received on the topic
 * @param {UseSubscribeOptions} options Options to pass to the subscription
 * @param options.enabled Whether to subscribe or not. Defaults to true
 * @param options.once Whether to unsubscribe after the first event. Defaults to false
 * @param options.unsubscribeOnUnmount Whether to unsubscribe on unmount. Defaults to true
 *
 * @returns A callback to subscribe to the topic
 *
 * @example
 * import { useState } from 'react';
 * import { useDataProvider } from 'react-admin';
 * import { useSubscribeCallback } from '@react-admin/ra-realtime';
 *
 * const LaunchBackgroundJob = () => {
 *     const dataProvider = useDataProvider();
 *     const [progress, setProgress] = useState(0);
 *     const subscribe = useSubscribeCallback(
 *         'backgroundJobs/recompute',
 *         event => setProgress(event.payload?.progress || 0)
 *     );
 *
 *     return (
 *         <div>
 *             <button onClick={() => {
 *                 subscribe();
 *                 dataProvider.recompute()
 *             }}>
 *                 Launch background job
 *             </button>
 *             {progress > 0 && <div>Progress: {progress}%</div>}
 *         </div>
 *     );
 * };
 *
 * @example // subscribe once
 * import { useDataProvider, useNotify } from 'react-admin';
 * import { useSubscribeCallback } from '@react-admin/ra-realtime';
 *
 * const LaunchBackgroundJob = () => {
 *     const dataProvider = useDataProvider();
 *     const notify = useNotify();
 *
 *     const subscribe = useSubscribeCallback(
 *         'backgroundJobs/recompute',
 *         event =>
 *             notify('Recompute complete: %{summary}', {
 *                 type: 'success',
 *                 messageArgs: {
 *                     summary: event.payload?.summary,
 *                 },
 *             }),
 *         {
 *             unsubscribeOnUnmount: false, // show the notification even if the user navigates away
 *             once: true, // unsubscribe after the first event
 *         }
 *     );
 *
 *     return (
 *         <button onClick={() => {
 *             subscribe();
 *             dataProvider.recompute()
 *         }}>
 *             Launch background job
 *         </button>
 *     );
 * };
 */
export var useSubscribeCallback = function (topic, callback, options) {
    var dataProvider = useDataProvider();
    var hasSubscribed = useRef(false);
    var _a = options || {}, _b = _a.once, once = _b === void 0 ? false : _b, _c = _a.enabled, enabled = _c === void 0 ? true : _c, _d = _a.unsubscribeOnUnmount, unsubscribeOnUnmount = _d === void 0 ? true : _d;
    var callbackWithUnsubscribe = useCallback(function (event) {
        var unsubscribe = function () {
            return dataProvider.unsubscribe(topic, callbackWithUnsubscribe);
        };
        callback(event, unsubscribe);
        if (once) {
            unsubscribe();
        }
    }, [callback, dataProvider, topic, once]);
    var subscribe = useCallback(function () {
        if (!enabled || !dataProvider || !topic || !callback) {
            return;
        }
        return dataProvider
            .subscribe(topic, callbackWithUnsubscribe)
            .then(function () {
            hasSubscribed.current = true;
        });
    }, [enabled, dataProvider, topic, callback, callbackWithUnsubscribe]);
    useEffect(function () {
        if (unsubscribeOnUnmount && hasSubscribed.current) {
            return function () {
                dataProvider.unsubscribe(topic, callbackWithUnsubscribe);
            };
        }
    }, [
        dataProvider,
        topic,
        callback,
        callbackWithUnsubscribe,
        unsubscribeOnUnmount,
    ]);
    return subscribe;
};
